import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import Vacancy from '../../models/vacancy.interface';
import { useTranslation } from 'react-i18next';
import LanguageOption from '../../models/language-option.interface';
import CandidateAdvancedFilter from './CandidateAdvancedFilter';
import './CandidateTableFilter.scss';

interface Props {
  filterByOffer: (filterType: string) => void;
  filterByStatus: (filterStatus: string) => void;
  selectedLanguageIds: string[];
  onToggleLanguage: (id: string) => void;
  selectedSkillIds: string[];
  onToggleSkill: (id: string) => void;
  selectedCities: string[];
  onToggleCity: (c: string) => void;
  unseenOnly: boolean;
  onToggleUnseen: () => void;
  minMatch: number | null;
  onSetMinMatch: (v: number | null) => void;
  daysAgo: number | null;
  onSetDaysAgo: (v: number | null) => void;
}

const CandidateTableFilter = ({
  filterByOffer,
  filterByStatus,
  selectedLanguageIds,
  onToggleLanguage,
  selectedSkillIds,
  onToggleSkill,
  selectedCities,
  onToggleCity,
  unseenOnly,
  onToggleUnseen,
  minMatch,
  onSetMinMatch,
  daysAgo,
  onSetDaysAgo,
}: Props) => {
  const { t } = useTranslation();
  const [selectedOffer, setSelectedOffer] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  const companyOffers = useSelector(
    (state: RootState) => state.companyReducer.offers
  );
  const languages = useSelector(
    (state: RootState) => state.languageReducer.languages
  );
  const filterOptions = useSelector(
    (state: RootState) => state.companyCandidatesReducer.filterOptions
  );

  const filteredOffers = companyOffers.filter(
    (offer) => offer.position !== '0' && offer.sector !== 'Internal Offer'
  );

  function handleSelectedOfferChange(
    event: React.ChangeEvent<HTMLSelectElement>
  ): void {
    const value = event.target.value;
    setSelectedOffer(value);
    filterByOffer(value);
  }

  function handleSelectedStatusChange(
    event: React.ChangeEvent<HTMLSelectElement>
  ): void {
    const value = event.target.value;
    setSelectedStatus(value);
    filterByStatus(value);
  }

  const [sliderDraft, setSliderDraft] = useState<number>(minMatch ?? 0);

  function handleSliderChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setSliderDraft(Number(e.target.value));
  }

  function handleSliderCommit(): void {
    onSetMinMatch(sliderDraft === 0 ? null : sliderDraft);
  }

  const sliderValue = sliderDraft;

  const advancedActiveCount =
    selectedSkillIds.length +
    selectedCities.length +
    selectedLanguageIds.length +
    (unseenOnly ? 1 : 0) +
    (daysAgo !== null ? 1 : 0) +
    (minMatch !== null ? 1 : 0);

  return (
    <div className='cand-filter'>
      {/* Row 1: search + main dropdowns + toggle */}
      <div className='d-flex align-items-center gap-3 flex-wrap'>
        <select
          className='form-select'
          value={selectedOffer}
          onChange={handleSelectedOfferChange}
        >
          <option value=''>{t('candidatesTablet.filters.vacancyDefault')}</option>
          {filteredOffers.map((offer: Vacancy, i: number) => (
            <option key={i} value={offer.id}>
              {offer.position}
            </option>
          ))}
        </select>

        <select
          className='form-select'
          value={selectedStatus}
          onChange={handleSelectedStatusChange}
        >
          <option value=''>{t('candidatesTablet.filters.allCandidates')}</option>
          <option value='pending'>{t('candidatesTablet.filters.pendingCandidates')}</option>
          <option value='seen'>{t('candidatesTablet.filters.seenCandidates')}</option>
          <option value='selected'>{t('candidatesTablet.filters.selectedCandidates')}</option>
          <option value='discarded'>{t('candidatesTablet.filters.discardedCandidates')}</option>
          <option value='withdrawn'>{t('candidatesTablet.filters.withdrawnCandidates')}</option>
        </select>

        <button
          className={`cand-filter__toggle ${showAdvanced ? 'cand-filter__toggle--open' : ''} ${advancedActiveCount > 0 ? 'cand-filter__toggle--active' : ''}`}
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {t('candidatesTablet.filters.advFilters')}
          {advancedActiveCount > 0 && ` (${advancedActiveCount})`}
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
            <polyline points='6 9 12 15 18 9' />
          </svg>
        </button>
      </div>

      {/* Collapsible advanced section */}
      {showAdvanced && (
        <div className='cand-filter__advanced'>
          {/* Multi-select filter buttons row */}
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <CandidateAdvancedFilter
              label={t('candidatesTablet.filters.advSkills')}
              sections={[{
                key: 'skills',
                titleKey: 'candidatesTablet.filters.advSkills',
                options: filterOptions.skills,
                selectedIds: selectedSkillIds,
                onToggle: onToggleSkill,
                searchable: true,
              }]}
              activeCount={selectedSkillIds.length}
            />

            <CandidateAdvancedFilter
              label={t('candidatesTablet.filters.advCity')}
              sections={[{
                key: 'city',
                titleKey: 'candidatesTablet.filters.advCity',
                options: filterOptions.cities.map((c) => ({ id: c, name: c })),
                selectedIds: selectedCities,
                onToggle: onToggleCity,
                searchable: true,
              }]}
              activeCount={selectedCities.length}
            />

            <CandidateAdvancedFilter
              label={t('candidatesTablet.filters.advLanguage')}
              sections={[{
                key: 'language',
                titleKey: 'candidatesTablet.filters.advLanguage',
                options: languages.map((l: LanguageOption) => ({ id: String(l.id), name: l.name })),
                selectedIds: selectedLanguageIds,
                onToggle: onToggleLanguage,
              }]}
              activeCount={selectedLanguageIds.length}
            />

            <CandidateAdvancedFilter
              label={t('candidatesTablet.filters.dateApplied')}
              sections={[{
                key: 'daysAgo',
                titleKey: 'candidatesTablet.filters.dateApplied',
                options: [
                  { id: '7',  name: t('candidatesTablet.filters.dateLast7Days') },
                  { id: '30', name: t('candidatesTablet.filters.dateLast30Days') },
                  { id: '90', name: t('candidatesTablet.filters.dateLast90Days') },
                ],
                selectedIds: daysAgo ? [String(daysAgo)] : [],
                onToggle: (id) => onSetDaysAgo(daysAgo === Number(id) ? null : Number(id)),
              }]}
              activeCount={daysAgo !== null ? 1 : 0}
            />

            <button
              type='button'
              className={`btn btn-sm ${unseenOnly ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={onToggleUnseen}
            >
              {t('candidatesTablet.filters.unseenOnly')}
            </button>
          </div>

          {/* AI Match slider */}
          <div className='cand-filter__slider-row'>
            <div className='cand-filter__slider-header'>
              <span>{t('candidatesTablet.filters.aiMatchLabel')}</span>
              <span className={`cand-filter__slider-value ${sliderValue === 0 ? 'cand-filter__slider-value--empty' : ''}`}>
                {sliderValue === 0 ? t('candidatesTablet.filters.aiMatchAny') : `${sliderValue}%+`}
              </span>
            </div>
            <div className='cand-filter__slider-track'>
              <span>0%</span>
              <input
                type='range'
                className='cand-filter__slider'
                min={0}
                max={100}
                step={5}
                value={sliderValue}
                style={{ '--fill': `${sliderValue}%` } as React.CSSProperties}
                onChange={handleSliderChange}
                onMouseUp={handleSliderCommit}
                onTouchEnd={handleSliderCommit}
              />
              <span>100%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateTableFilter;
